import { useRef } from "react";
import { useApi } from "@platform/api";
import { useQueries } from "@tanstack/react-query";

import { getBenefitPlanFields } from "../api/client";

import type {
  BenefitFieldMeta,
  BenefitPlanFieldsResponse,
  EnrichedBenefitFieldMeta,
  PlanFieldsMeta,
} from "../api/benefit-plan-fields-types";

const BENEFIT_PLAN_FIELDS_QUERY_KEY = "benefitPlanFields";

export const getBenefitPlanFieldsCacheKey = (documentId: string): string[] => [
  BENEFIT_PLAN_FIELDS_QUERY_KEY,
  documentId,
];

/**
 * Lookup key for matching AI metadata to AG Grid cells.
 * Format: `btxIdentifier::btxFieldName` or `btxIdentifier::btxFieldName::network`
 */
export function buildFieldLookupKey(
  btxIdentifier: string,
  btxFieldName: string,
  network?: string,
): string {
  return network
    ? `${btxIdentifier}::${btxFieldName}::${network}`
    : `${btxIdentifier}::${btxFieldName}`;
}

export type AiFieldLookup = Record<string, EnrichedBenefitFieldMeta>;

/** Maps plan UUID (btxIdentifier) to source document filename(s). */
export type PlanDocumentMap = Record<string, string[]>;

export interface BenefitPlanFieldsData {
  isLoading: boolean;
  /** Flat lookup keyed by `btxIdentifier::btxFieldName[::network]` */
  fieldLookup: AiFieldLookup;
  /** Maps plan UUID (btxIdentifier) → source document filename(s) */
  planDocumentMap: PlanDocumentMap;
}

/**
 * Suffixes that `buildTypeAmountBenefitPair` appends to an AI Dox base
 * field name (the `prefix`) when generating flat grid `benefitKey` values.
 *
 * For each AI field (e.g. `individualDeductible`) the lookup is expanded
 * with keys like `individualDeductibleType`, `individualDeductibleAmount`,
 * etc. so the cell renderer can match directly without normalizing at
 * render-time.
 */
const BENEFIT_KEY_SUFFIXES = [
  "Type",
  "Amount",
  "Age",
  "Months",
  "MonthsDetail",
  "Days",
  "DollarAmount",
  "SalaryMultiplier",
  "AdditionalAmount",
  "AverageMonths",
  "Schedules",
];

const RATE_TOOLTIP_FIELDS = new Set([
  "rateSchedule",
  "enrolledLives",
  "renewalRateSchedule",
  "renewalEnrolledLives",
]);

function isRatesFieldName(fieldName: string): boolean {
  return RATE_TOOLTIP_FIELDS.has(fieldName);
}

// Rate and plan-admin fields carry no benefit-detail content. A rates document
// that cannot be matched to an existing plan produces a plan whose fields are
// all of this kind. Such a rate-only plan may still inherit dependent fields
// from a same-class plan, but it must never be filled by the cross-class
// single-source fallback — "a rates doc creates a blank plan if it cannot be
// related to an existing plan" (CD-2827).
const NON_BENEFIT_FIELD_NAMES = new Set<string>([
  ...RATE_TOOLTIP_FIELDS,
  "rateGuarantee",
  "participationRequirements",
  "commission",
]);

function planHasBenefitDetailField(plan: PlanFieldsMeta): boolean {
  // A field with no btxFieldName does not map to any grid cell (e.g. a rate
  // document's "Past Rate" or prefixed "Rate Guarantee"), so it is not benefit
  // content and must not make a rate-only plan look benefit-bearing (CD-2827).
  return plan.fields.some(
    (field) =>
      field.btxFieldName !== "" &&
      !NON_BENEFIT_FIELD_NAMES.has(field.btxFieldName),
  );
}

function parseIsoTimestamp(value: string | null): number | null {
  if (!value) {
    return null;
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function preferByBenefitPlanCreatedAtForRates(
  candidate: EnrichedBenefitFieldMeta,
  existing: EnrichedBenefitFieldMeta,
): boolean {
  const candidateTimestamp =
    parseIsoTimestamp(candidate.benefitPlanCreatedAt) ??
    Number.NEGATIVE_INFINITY;

  const existingTimestamp =
    parseIsoTimestamp(existing.benefitPlanCreatedAt) ??
    Number.NEGATIVE_INFINITY;

  if (candidateTimestamp === existingTimestamp) {
    return isMoreInformative(candidate, existing);
  }

  return candidateTimestamp > existingTimestamp;
}

/**
 * Returns true when `candidate` is more informative than `existing`.
 * Prefers non-empty values and values other than "NOT_FOUND".
 * When both values are equally usable, prefers richer metadata
 * (documentDownloadUrl, pageNumber, justification).
 */
const isEmpty = (v: string): boolean => !v || v.trim() === "";
const isNotFound = (v: string): boolean => v === "NOT_FOUND";

function isMoreInformative(
  candidate: EnrichedBenefitFieldMeta,
  existing: EnrichedBenefitFieldMeta,
): boolean {
  const existingUsable =
    !isEmpty(existing.value) && !isNotFound(existing.value);
  const candidateUsable =
    !isEmpty(candidate.value) && !isNotFound(candidate.value);

  // Clear winner: one is usable, the other is not
  if (candidateUsable && !existingUsable) return true;
  if (!candidateUsable) return false;

  // Both usable — use metadata richness as tie-breaker
  const metadataScore = (f: EnrichedBenefitFieldMeta): number =>
    (f.documentDownloadUrl ? 1 : 0) +
    (f.pageNumber != null ? 1 : 0) +
    (!isEmpty(f.justification) ? 1 : 0) +
    (!isEmpty(f.planName) ? 1 : 0);

  return metadataScore(candidate) > metadataScore(existing);
}

/**
 * A RUMI citation describes the value RUMI wrote to btx, so it wins over
 * extraction output for the same field, whichever document either came from,
 * as long as it carries a value: a blank citation must not hide usable
 * extraction evidence behind an empty card.
 */
const isRumiCitation = (field: BenefitFieldMeta): boolean =>
  field.source === "rumi";

/** A result ai-dox built from citations alone, with no extraction plan behind it. */
const isCitationOnlyPlan = (plan: PlanFieldsMeta): boolean =>
  plan.benefitPlanId === "";

const hasUsableValue = (field: BenefitFieldMeta): boolean =>
  !isEmpty(field.value) && !isNotFound(field.value);

const rumiWinsAcrossSources = (
  candidate: EnrichedBenefitFieldMeta,
  existing: EnrichedBenefitFieldMeta,
): boolean =>
  isRumiCitation(candidate)
    ? hasUsableValue(candidate)
    : !hasUsableValue(existing);

/** Insert a key into the lookup, preferring the more informative value. */
function setPreferred(
  lookup: AiFieldLookup,
  key: string,
  field: EnrichedBenefitFieldMeta,
): void {
  const existing = lookup[key];

  const shouldReplace = !existing
    ? true
    : field.source !== existing.source
      ? rumiWinsAcrossSources(field, existing)
      : isRatesFieldName(field.btxFieldName) &&
          isRatesFieldName(existing.btxFieldName)
        ? preferByBenefitPlanCreatedAtForRates(field, existing)
        : isMoreInformative(field, existing);

  if (shouldReplace) {
    lookup[key] = field;
  }
}

/**
 * Converts a dot-separated field path to camelCase.
 * e.g. "employee.benefitDescription" → "employeeBenefitDescription"
 * Returns undefined if the name contains no dots (no conversion needed).
 */
export function dotPathToCamelCase(name: string): string | undefined {
  if (!name.includes(".")) return undefined;
  const parts = name.split(".");
  return parts
    .map((part, i) =>
      i === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1),
    )
    .join("");
}

/** Insert keys (primary + suffixed, with/without network) for a single base name. */
function insertKeysForName(
  lookup: AiFieldLookup,
  btxIdentifier: string,
  baseName: string,
  field: EnrichedBenefitFieldMeta,
): void {
  setPreferred(
    lookup,
    buildFieldLookupKey(btxIdentifier, baseName, field.network ?? undefined),
    field,
  );

  if (field.network) {
    setPreferred(lookup, buildFieldLookupKey(btxIdentifier, baseName), field);
  }

  for (const suffix of BENEFIT_KEY_SUFFIXES) {
    const suffixedName = `${baseName}${suffix}`;
    setPreferred(
      lookup,
      buildFieldLookupKey(
        btxIdentifier,
        suffixedName,
        field.network ?? undefined,
      ),
      field,
    );

    if (field.network) {
      setPreferred(
        lookup,
        buildFieldLookupKey(btxIdentifier, suffixedName),
        field,
      );
    }
  }
}

/**
 * Explicit btxFieldName → grid key prefix aliases for Voluntary Life, where the
 * aidox btx_field_id and the grid key use different word orders, so a raw-name
 * match against the grid key fails. The ids are a mix — some person-first
 * ("employeeBenefitDescription"), some benefit-first ("benefitMaximumChild") —
 * and each entry maps the id to the grid key(s) it should light up.
 *
 * When one btx_field_id maps to multiple grid keys, use an array. The child
 * student-limiting-age id splits by leaf: the numeric age
 * ("childStudentLimitingAgeAmount" → "childLimitingAge") and the status enum
 * ("childStudentLimitingAgeType" → "studentStatusStudentLimitingAge").
 *
 * Not aliased (no 1:1 grid mapping):
 *   childBenefitAmountsByAge → maps to three age-range fields
 */
/**
 * Derived show/hide toggle keys: alias fan-outs onto these mark provenance
 * only (the extraction maps into a different row's cell).
 */
const INDICATOR_ONLY_GRID_KEYS = new Set([
  "spouseBenefitsIncluded",
  "childBenefitsIncluded",
  "dependentBenefitsIncluded",
]);

export const BTX_FIELD_ALIASES: Record<string, string | string[]> = {
  spouseBenefit: "dependentBenefitsIncluded",
  childBenefit: "dependentBenefitsIncluded",
  employeeBenefitDescription: "benefitDescriptionEmployee",
  employeeGuaranteeIssue: [
    "guaranteeIssueEmployee",
    "guaranteeIssueEmployeeAgesDetails",
  ],
  spouseBenefitDescription: [
    "benefitDescriptionSpouse",
    "spouseBenefitsIncluded",
  ],
  spouseBenefitMaximum: ["benefitMaximumSpouse", "spouseBenefitsIncluded"],
  guaranteeIssueSpouse: [
    "guaranteeIssueSpouse",
    "guaranteeIssueSpouseAgesDetails",
    "spouseBenefitsIncluded",
  ],
  amountNotToExceedSpouse: [
    "amountNotToExceedSpouse",
    "spouseBenefitsIncluded",
  ],
  benefitMaximumChild: ["benefitMaximumChild", "childBenefitsIncluded"],
  amountNotToExceedChild: ["amountNotToExceedChild", "childBenefitsIncluded"],
  childStudentLimitingAgeAmount: ["childLimitingAge", "childBenefitsIncluded"],
  childStudentLimitingAgeType: [
    "studentStatusStudentLimitingAge",
    "childBenefitsIncluded",
  ],
  benefitDescriptionChildDependentAgesBirthTo14Days: "childBenefitsIncluded",
  benefitDescriptionChildDependentAges15DaysTo6Months: "childBenefitsIncluded",
  benefitDescriptionChildDependentAges6MonthsToLimitingAge:
    "childBenefitsIncluded",
  adndTiedToLife: "adndTiedToLifeElection",
};

/** Insert all key variants (with/without network, with suffixes) for a single field. */
function insertFieldVariants(
  lookup: AiFieldLookup,
  btxIdentifier: string,
  field: EnrichedBenefitFieldMeta,
): void {
  // Register under the exact AI Dox field name
  insertKeysForName(lookup, btxIdentifier, field.btxFieldName, field);

  // AI Dox uses dot-notation for nested fields (e.g. "employee.benefitDescription")
  // but the grid uses camelCase benefitKeys (e.g. "employeeBenefitDescription").
  // Register under the camelCase equivalent so cells can match.
  const camelCaseName = dotPathToCamelCase(field.btxFieldName);
  if (camelCaseName) {
    insertKeysForName(lookup, btxIdentifier, camelCaseName, field);
  }

  // Some LOCs swap the person/benefit order in grid keys
  // (e.g. "benefitDescriptionEmployee" instead of "employeeBenefitDescription").
  // Register under the explicit alias(es) when one exists. Fan-outs onto the
  // FE-only "Benefits Included" toggles only mark provenance — the extraction
  // belongs to another row — so they carry indicatorOnly and never raise a
  // mapping error there.
  const alias = BTX_FIELD_ALIASES[field.btxFieldName];
  if (alias) {
    const names = Array.isArray(alias) ? alias : [alias];
    for (const name of names) {
      const entry = INDICATOR_ONLY_GRID_KEYS.has(name)
        ? { ...field, indicatorOnly: true }
        : field;
      insertKeysForName(lookup, btxIdentifier, name, entry);
    }
  }
}

interface LookupResult {
  fieldLookup: AiFieldLookup;
  planDocumentMap: PlanDocumentMap;
}

const ELIGIBILITY_CLASS_JUSTIFICATION =
  "Eligibility class derived from AI-extracted plan class name.";

const ELIGIBILITY_CLASS_NOT_FOUND_JUSTIFICATION =
  'Eligibility Class not found. Defaulted to "All Eligible Employees".';

const AI_NOT_FOUND_VALUE = "NOT_FOUND";

/**
 * ai-dox substitutes the plan name "Default" when an extracted field carried no
 * class name at all, so a plan named that marks a class the AI never found
 * rather than one it read off the document.
 */
const AI_DOX_MISSING_CLASS_NAME = "default";

function aiFoundNoEligibilityClass(planName: string): boolean {
  const normalized = normalizePlanClassName(planName);
  return normalized === "" || normalized === AI_DOX_MISSING_CLASS_NAME;
}

const CLASS_SHARED_COVERAGES = new Set(["voluntaryLifeAdnd"]);

function normalizePlanClassName(planName: string): string {
  return planName.trim().replace(/\s+/g, " ").toLowerCase();
}

// A field is a shareable dependent (spouse/child) field when its alias fans out
// to a "Benefits Included" toggle. Driving this off BTX_FIELD_ALIASES keeps it
// in step with the alias table instead of restating the field names here.
function shouldShareDependentFieldAcrossClassPlans(fieldName: string): boolean {
  const alias = BTX_FIELD_ALIASES[fieldName];

  if (alias === undefined) return false;

  const gridKeys = Array.isArray(alias) ? alias : [alias];

  return gridKeys.some((gridKey) => INDICATOR_ONLY_GRID_KEYS.has(gridKey));
}

function addPlanDocumentMetadata(
  fieldLookup: AiFieldLookup,
  planDocumentMap: PlanDocumentMap,
  btxIdentifier: string,
  plan: PlanFieldsMeta,
  documentName: string | null,
  downloadUrl: string | null,
): void {
  const benefitPlanCreatedAt =
    typeof plan.benefitPlanCreatedAt === "string"
      ? plan.benefitPlanCreatedAt
      : null;

  if (!documentName) {
    return;
  }

  if (!planDocumentMap[btxIdentifier]) {
    planDocumentMap[btxIdentifier] = [];
  }
  if (!planDocumentMap[btxIdentifier].includes(documentName)) {
    planDocumentMap[btxIdentifier].push(documentName);
  }

  // A plan only RUMI cited carries RUMI's plan name, not a class extraction
  // found, so it says nothing about eligibility. ai-dox synthesizes such a
  // result without an extraction plan behind it, which is what the empty
  // benefitPlanId marks; judging by the fields instead would drop the class
  // evidence of an extracted plan as soon as RUMI re-cites all its fields.
  if (isCitationOnlyPlan(plan)) {
    return;
  }

  // Eligibility classes are represented as plan-level class names rather than
  // extracted field rows, so synthesize one lookup entry per plan. As we
  // process documents, setPreferred retains the most informative source
  // metadata for this single key so the grid can render notation context.
  //
  // When the AI found no class, the entry reports the not-found sentinel so the
  // popover shows a dash for the value it never extracted, while the cell keeps
  // showing whichever class the RFP defaulted the plan to (DP-4091).
  const classNotFound = aiFoundNoEligibilityClass(plan.planName);

  setPreferred(
    fieldLookup,
    buildFieldLookupKey(btxIdentifier, "eligibilityClasses"),
    {
      btxFieldName: "eligibilityClasses",
      value: classNotFound ? AI_NOT_FOUND_VALUE : plan.planName,
      pageNumber: null,
      justification: classNotFound
        ? ELIGIBILITY_CLASS_NOT_FOUND_JUSTIFICATION
        : ELIGIBILITY_CLASS_JUSTIFICATION,
      network: null,
      networkName: null,
      optionName: null,
      highAccuracy: false,
      source: "extraction",
      documentDownloadUrl: downloadUrl,
      benefitPlanCreatedAt,
      planName: plan.planName,
      documentName,
    },
  );
}

function trackClassPlanId(
  classPlanIds: Map<string, Set<string>>,
  btxIdentifier: string,
  plan: PlanFieldsMeta,
): boolean {
  const normalizedClassName = normalizePlanClassName(plan.planName);
  const shouldTrackClassSharing =
    CLASS_SHARED_COVERAGES.has(plan.coverageType) &&
    normalizedClassName !== "" &&
    normalizedClassName !== "default";

  if (!shouldTrackClassSharing) {
    return false;
  }

  const ids = classPlanIds.get(normalizedClassName) ?? new Set<string>();
  ids.add(btxIdentifier);
  classPlanIds.set(normalizedClassName, ids);
  return true;
}

function addPlanFieldsToLookup(
  fieldLookup: AiFieldLookup,
  dependentFieldsByPlan: Map<string, EnrichedBenefitFieldMeta[]>,
  btxIdentifier: string,
  plan: PlanFieldsMeta,
  downloadUrl: string | null,
  documentName: string | null,
  shouldTrackClassSharing: boolean,
): void {
  const benefitPlanCreatedAt =
    typeof plan.benefitPlanCreatedAt === "string"
      ? plan.benefitPlanCreatedAt
      : null;

  for (const field of plan.fields) {
    const enrichedField = {
      ...field,
      documentDownloadUrl: downloadUrl,
      benefitPlanCreatedAt,
      planName: plan.planName,
      documentName,
    };
    insertFieldVariants(fieldLookup, btxIdentifier, enrichedField);

    if (
      shouldTrackClassSharing &&
      shouldShareDependentFieldAcrossClassPlans(field.btxFieldName)
    ) {
      const existing = dependentFieldsByPlan.get(btxIdentifier) ?? [];
      existing.push(enrichedField);
      dependentFieldsByPlan.set(btxIdentifier, existing);
    }
  }
}

function mirrorDependentFieldsToTargetPlan(
  sourcePlanId: string,
  targetPlanId: string,
  planNameById: Map<string, string>,
  dependentFieldsByPlan: Map<string, EnrichedBenefitFieldMeta[]>,
  fieldLookup: AiFieldLookup,
): void {
  if (targetPlanId === sourcePlanId) {
    return;
  }

  const targetPlanName = planNameById.get(targetPlanId);
  if (!targetPlanName) {
    return;
  }

  const dependentFields = dependentFieldsByPlan.get(sourcePlanId) ?? [];
  for (const dependentField of dependentFields) {
    insertFieldVariants(fieldLookup, targetPlanId, {
      ...dependentField,
      planName: targetPlanName,
    });
  }
}

function shareDependentFieldsWithinClassGroup(
  groupedPlanIds: string[],
  planNameById: Map<string, string>,
  dependentFieldsByPlan: Map<string, EnrichedBenefitFieldMeta[]>,
  fieldLookup: AiFieldLookup,
): void {
  for (const sourcePlanId of groupedPlanIds) {
    const dependentFields = dependentFieldsByPlan.get(sourcePlanId) ?? [];
    if (dependentFields.length === 0) {
      continue;
    }

    for (const targetPlanId of groupedPlanIds) {
      mirrorDependentFieldsToTargetPlan(
        sourcePlanId,
        targetPlanId,
        planNameById,
        dependentFieldsByPlan,
        fieldLookup,
      );
    }
  }
}

function shareDependentFieldsWithSingleSourceFallback(
  trackedClassSharePlanIds: Set<string>,
  benefitBearingPlanIds: Set<string>,
  planNameById: Map<string, string>,
  dependentFieldsByPlan: Map<string, EnrichedBenefitFieldMeta[]>,
  fieldLookup: AiFieldLookup,
): void {
  const trackedPlanIds = [...trackedClassSharePlanIds];
  const sourcePlanIds = trackedPlanIds.filter(
    (planId) => (dependentFieldsByPlan.get(planId) ?? []).length > 0,
  );

  if (sourcePlanIds.length !== 1) {
    return;
  }

  const sourcePlanId = sourcePlanIds[0];
  for (const targetPlanId of trackedPlanIds) {
    if (targetPlanId === sourcePlanId) {
      continue;
    }

    if ((dependentFieldsByPlan.get(targetPlanId) ?? []).length > 0) {
      continue;
    }

    // Only a plan with its own benefit detail can be the incumbent this source
    // relates to. A rate-only plan in a different class cannot be related to an
    // existing plan, so it stays blank rather than inheriting these fields
    // across classes (CD-2827).
    if (!benefitBearingPlanIds.has(targetPlanId)) {
      continue;
    }

    mirrorDependentFieldsToTargetPlan(
      sourcePlanId,
      targetPlanId,
      planNameById,
      dependentFieldsByPlan,
      fieldLookup,
    );
  }
}

function shareDependentFieldsAcrossClassPlans(
  classPlanIds: Map<string, Set<string>>,
  trackedClassSharePlanIds: Set<string>,
  benefitBearingPlanIds: Set<string>,
  planNameById: Map<string, string>,
  dependentFieldsByPlan: Map<string, EnrichedBenefitFieldMeta[]>,
  fieldLookup: AiFieldLookup,
): void {
  for (const planIds of classPlanIds.values()) {
    if (planIds.size < 2) {
      continue;
    }

    shareDependentFieldsWithinClassGroup(
      [...planIds],
      planNameById,
      dependentFieldsByPlan,
      fieldLookup,
    );
  }

  shareDependentFieldsWithSingleSourceFallback(
    trackedClassSharePlanIds,
    benefitBearingPlanIds,
    planNameById,
    dependentFieldsByPlan,
    fieldLookup,
  );
}

/** @internal Exported for testing only. */
export function buildLookup(
  responses: BenefitPlanFieldsResponse[],
  documentDownloadUrls?: Record<string, string>,
): LookupResult {
  const fieldLookup: AiFieldLookup = {};
  const planDocumentMap: PlanDocumentMap = {};
  const classPlanIds = new Map<string, Set<string>>();
  const trackedClassSharePlanIds = new Set<string>();
  const benefitBearingPlanIds = new Set<string>();
  const planNameById = new Map<string, string>();
  const dependentFieldsByPlan = new Map<string, EnrichedBenefitFieldMeta[]>();

  for (const response of responses) {
    const downloadUrl = documentDownloadUrls?.[response.documentId] ?? null;

    for (const [btxIdentifier, plan] of Object.entries(response.plans)) {
      planNameById.set(btxIdentifier, plan.planName);

      const shouldTrackClassSharing = trackClassPlanId(
        classPlanIds,
        btxIdentifier,
        plan,
      );

      if (shouldTrackClassSharing) {
        trackedClassSharePlanIds.add(btxIdentifier);
      }

      if (planHasBenefitDetailField(plan)) {
        benefitBearingPlanIds.add(btxIdentifier);
      }

      addPlanDocumentMetadata(
        fieldLookup,
        planDocumentMap,
        btxIdentifier,
        plan,
        response.documentName,
        downloadUrl,
      );

      addPlanFieldsToLookup(
        fieldLookup,
        dependentFieldsByPlan,
        btxIdentifier,
        plan,
        downloadUrl,
        response.documentName,
        shouldTrackClassSharing,
      );
    }
  }

  shareDependentFieldsAcrossClassPlans(
    classPlanIds,
    trackedClassSharePlanIds,
    benefitBearingPlanIds,
    planNameById,
    dependentFieldsByPlan,
    fieldLookup,
  );

  return { fieldLookup, planDocumentMap };
}

/** AI extraction metadata for a single rate-area field, keyed by plan ID. */
export interface AiPlanRateFieldMeta {
  planName?: string;
  value: string;
  pageNumber: number | null;
  justification: string;
  documentDownloadUrl?: string | null;
  benefitPlanCreatedAt?: string | null;
  documentName?: string | null;
}

/** Combined AI extraction metadata for a single plan's rate-area fields. */
export interface AiPlanRateFieldsMeta {
  rate?: AiPlanRateFieldMeta;
  enrolledLives?: AiPlanRateFieldMeta;
}

/**
 * Builds a per-plan lookup of combined AI extraction metadata (rate +
 * enrolled lives) for RatesGrid's AI indicators. Shared by the Build tab
 * (request-side benefit plans) and the Present tab (proposal plans) — both
 * key `fieldLookup` by the same `btxIdentifier` (plan UUID) space.
 */
export function buildAiFieldsByPlanId(
  fieldLookup: AiFieldLookup,
  planIds: string[],
): Record<string, AiPlanRateFieldsMeta> | undefined {
  if (Object.keys(fieldLookup).length === 0) return undefined;
  const result: Record<string, AiPlanRateFieldsMeta> = {};

  const toMeta = (key: string): AiPlanRateFieldMeta | undefined => {
    const field = fieldLookup[key];
    return field
      ? {
          planName: field.planName,
          value: field.value,
          pageNumber: field.pageNumber,
          justification: field.justification,
          documentDownloadUrl: field.documentDownloadUrl,
          benefitPlanCreatedAt: field.benefitPlanCreatedAt,
          documentName: field.documentName,
        }
      : undefined;
  };

  for (const planId of planIds) {
    const rate = toMeta(buildFieldLookupKey(planId, "rateSchedule"));
    const enrolledLives = toMeta(buildFieldLookupKey(planId, "enrolledLives"));
    if (rate || enrolledLives) {
      result[planId] = { rate, enrolledLives };
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

export function useBenefitPlanFields(
  documentIds: string[],
  options?: {
    enabled?: boolean;
    documentDownloadUrls?: Record<string, string>;
    refetchInterval?: number | false;
  },
): BenefitPlanFieldsData {
  const axiosInstance = useApi();
  const enabled = options?.enabled ?? true;
  const documentDownloadUrls = options?.documentDownloadUrls;

  const queries = useQueries({
    queries: documentIds.map((documentId) => ({
      queryKey: getBenefitPlanFieldsCacheKey(documentId),
      queryFn: () => getBenefitPlanFields(documentId, axiosInstance),
      enabled: enabled && documentIds.length > 0,
      refetchInterval: options?.refetchInterval,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);

  // useQueries returns a new array reference every render. Recompute the
  // lookup only when a query's data or the download URLs change. Data
  // references are compared rather than dataUpdatedAt: a refetch that returns
  // the same fields keeps its data reference (structural sharing) but still
  // bumps dataUpdatedAt, and every Quotes cell re-renders on a new lookup.
  const cacheRef = useRef<{
    data: (BenefitPlanFieldsResponse | undefined)[];
    urls: Record<string, string> | undefined;
    result: LookupResult;
  }>({
    data: [],
    urls: undefined,
    result: { fieldLookup: {}, planDocumentMap: {} },
  });

  // A failed background refetch keeps the last successful data, so reading
  // `data` directly keeps the AI indicators showing through a transient error.
  const queryData = queries.map((q) => q.data);

  if (
    cacheRef.current.urls !== documentDownloadUrls ||
    queryData.length !== cacheRef.current.data.length ||
    queryData.some((data, index) => data !== cacheRef.current.data[index])
  ) {
    const successful = queryData.filter(
      (data): data is BenefitPlanFieldsResponse => data != null,
    );
    cacheRef.current = {
      data: queryData,
      urls: documentDownloadUrls,
      result: buildLookup(successful, documentDownloadUrls),
    };
  }

  const { fieldLookup, planDocumentMap } = cacheRef.current.result;

  return { isLoading, fieldLookup, planDocumentMap };
}
